import {DataItem, Series} from "@swimlane/ngx-charts";
import {AccelerationCount, AggregatedAnimalActivity} from "src/app/domain/assets/animals/aggregated-animal-activity";
import {
  Context
} from "src/app/presentation/main-view/information-center/animals/animal-details/animal-activity/animal-activity.component";

export const average = (seriesPerAnimal: Map<string, DataItem[]>, animalId: string): DataItem[] => {
  const deltasPerDate = new Map();
  seriesPerAnimal.forEach((dataItems: DataItem[], currentAnimalId: string) => {
    if (animalId === currentAnimalId) return;

    dataItems.forEach(dataItem => {
      const deltas = deltasPerDate.get(dataItem.name.toString()) || [];
      deltas.push(dataItem.value);
      deltasPerDate.set(dataItem.name.toString(), deltas);
    });
  });

  const result: DataItem[] = [];
  deltasPerDate.forEach((deltas: number[], date: string) => {
    if (deltas.length === 0) return;

    const average = deltas.reduce((x, y) => x + y, 0) / deltas.length;

    result.push({
      name: new Date(date),
      value: average
    });
  });

  return result;
}

export const sort = (accelerationCounts: AccelerationCount[]) => {
  accelerationCounts.sort((a, b) => a.instant.getTime() - b.instant.getTime());
}

export const calculateSerie = (accelerationCounts: AccelerationCount[]): DataItem[] => {
  sort(accelerationCounts);

  const result: DataItem[] = [];

  for (let i = 1; i < accelerationCounts.length; i++) {
    const current = accelerationCounts[i];
    const previous = accelerationCounts[i - 1];

    const currentTime = current.instant.getTime();
    const previousTime = previous.instant.getTime();
    if (currentTime === previousTime) continue;

    const delta = current.numberOfInterrupts - previous.numberOfInterrupts;
    if (delta < 0) continue;

    const deltaPerMinute = delta / ((currentTime - previousTime) / 1000 / 60);

    result.push({
      name: new Date(current.instant),
      value: deltaPerMinute
    });
  }

  return result;
}

export const calculateSeriesWithoutContext = (accelerationCounts: AccelerationCount[]) => {
 if (!accelerationCounts) return [];

 return [
  {
    name: "Placeholder",
    series: []
  },
  {
    name: "Accelerations per minute",
    series: calculateSerie(accelerationCounts)
  }
 ];
}

export const calculateSeriesWithHerd = (accelerationCountsPerAnimal: Map<string, AccelerationCount[]>, animalId: string) => {
  const seriePerAnimal = new Map();
  accelerationCountsPerAnimal.forEach((accelerationCounts: AccelerationCount[], animalId: string) => {
    seriePerAnimal.set(animalId, calculateSerie(accelerationCounts));
  });

  const serieForAnimal = seriePerAnimal.get(animalId);
  if (!serieForAnimal) return [];

  return [
    {
      name: "Average accelerations per minute of herd",
      series: average(seriePerAnimal, animalId)
    },
    {
      name: "Accelerations per minute",
      series: serieForAnimal
    }
  ];
}

export const calculateSeriesWithHistoricalActivity = (accelerationCounts: AccelerationCount[], end: number, selectedDateRange: number) => {
  if (!accelerationCounts) return [];

  const currentActivity: AccelerationCount[] = [];
  const historicalActivity: AccelerationCount[] = [];
  const threshold = end - selectedDateRange;
  accelerationCounts.forEach(accelerationCount => {
    const timestamp = accelerationCount.instant.getTime()
    if (timestamp < threshold) {
      accelerationCount.instant = new Date(timestamp + selectedDateRange);
      historicalActivity.push(accelerationCount);
    } else {
      currentActivity.push(accelerationCount);
    }
  });

  return [
    {
      name: "Accelerations per minute in previous period",
      series: calculateSerie(historicalActivity)
    },
    {
      name: "Accelerations per minute",
      series: calculateSerie(currentActivity)
    }
  ];
}

export const calculateSeries = (aggregatedActivities: AggregatedAnimalActivity[], animalId: string, end: number, selectedDateRange: number, context?: Context): Series[] => {
  const accelerationCountsPerAnimal = new Map();
  aggregatedActivities.forEach(activity => {
    const counts = accelerationCountsPerAnimal.get(activity.animalId) || [];
    counts.push(...activity.accelerationCounts);
    accelerationCountsPerAnimal.set(activity.animalId, counts);
  });

  switch (context) {
    case undefined: {
      return calculateSeriesWithoutContext(accelerationCountsPerAnimal.get(animalId));
    }
    case Context.HERD: {
      return calculateSeriesWithHerd(accelerationCountsPerAnimal, animalId);
    }
    case Context.HISTORICAL: {
      return calculateSeriesWithHistoricalActivity(accelerationCountsPerAnimal.get(animalId), end, selectedDateRange);
    }
  }

}
